import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { useRowSelect, useSortBy, useTable, usePagination } from "react-table";
import { sumBy, capitalize } from "lodash";
import { Button, ModalBody, ModalHeader, Nav, Row, Input, FormGroup, Label } from "reactstrap";
import DatePicker from "react-datepicker";
import { useTranslation, Trans } from "react-i18next";
import { useParams, useRouteMatch } from "react-router";
import produce from "immer";
import cx from "classnames";
import { NotificationManager } from "react-notifications";
import * as XLSX from 'xlsx';
import ImportModal, { readFileAsync } from "components/ImportModal";
import { useXeroClient, useConfirmModal } from "hooks";
import {
  XeroPagination,
  SelectionCell,
  SelectionHeader,
  Table,
  DateCell,
  CurrencyCell,
} from "components/react-table";
import { Page, LoadingMsg } from "components/common";
import { Link } from "react-router-dom";
import { mjNotes as importantNotes } from "pages/UserList/modelNames";
import { CurrencySymbol, renderCountSpan } from "services/util";
import BackButton from "components/refactoring/BackButton";
import { formatXeroDate } from "services/util";

const initialSortBy = [
  {
    id: "Date",
    desc: true,
  },
];

export const ManualJournalsList = ({ history }) => {
  const xero = useXeroClient();
  const { t } = useTranslation();
  const { status } = useParams();
  const match = useRouteMatch();
  const [data, onSuccess] = useState([]);
  const isAll = status === "all";
  const { Modal: PostModal, toggle: togglePost } = useConfirmModal();
  const { Modal: DeleteModal, toggle: toggleDelete } = useConfirmModal();
  const { Modal: VoidModal, toggle: toggleVoid } = useConfirmModal();
  const [allManualJournals, setAllManualJournals] = useState()

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true)

  const [search, setSearch] = useState({})
  const [reload, setReload] = useState(false)

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const { data: organisation } = useQuery("organisations", () =>
    xero.get("organisations").get("data").get(0)
  );


  const extraHandling = (data) => {
    const mjList = data?.reduce((acc, item) => {
      const { Status } = item;
      if (Status === 'POSTED') {
        acc.Posted.push(item);
      } else if (Status === 'VOIDED') {
        acc.Voided.push(item);
      } else if (Status === 'DRAFT') {
        acc.Draft.push(item);
      } else if (Status === 'ARCHIVED') {
        acc.Archived.push(item);
      } else {
        acc.Others.push(item);
      }
      return acc;
    }, { Posted: [], Voided: [], Draft: [], Archived: [], Others: [] });
    setAllManualJournals(mjList)
    onSuccess(data)
  }

  const loading = allManualJournals === undefined

  const columns = useMemo(
    () =>
      organisation
        ? [
          ...(!isAll
            ? [
              {
                id: "selection",
                Cell: SelectionCell,
                Header: SelectionHeader,
              },
            ]
            : []),
          {
            Header: t("Narration"),
            accessor: "Narration",
            Cell: ({ value, row: { original } }) => (
              <Link to={`${match.url}/${original.ManualJournalID}`}>
                {value}
              </Link>
            ),
          },
          {
            Header: t("Date"),
            accessor: "Date",
            Cell: DateCell,
          },
          {
            Header: t("Debit"),
            accessor: "Debit",
            Cell: CurrencyCell,
            disableSortBy: true,
          },
          {
            Header: t("Credit"),
            accessor: "Credit",
            Cell: CurrencyCell,
            disableSortBy: true,
          },
          ...(isAll
            ? [
              {
                Header: t("Status"),
                accessor: "Status",
                Cell: ({ value }) => {
                  return (
                    <span
                      className={cx({
                        "text-warning": value === "DRAFT",
                        "text-success": value === "POSTED",
                        "text-danger": value === "VOIDED",
                        "text-muted": value === "ARCHIVED",
                      })}
                    >
                      {t(capitalize(value))}
                    </span>
                  );
                },
              },
            ]
            : []),
        ]
        : [],
    [t, organisation, isAll]
  );

  const _search = window.location.search;
  const params = new URLSearchParams(_search);
  const _pageSize = params.get('pageSize') || 0
  const _page = params.get('page') || 1

  const table = useTable(
    {
      initialState: {
        pageSize: _pageSize || 25,
        sortBy: initialSortBy,
        page: _page || 1
      },
      disableMultiSort: true,
      data,
      columns,
      noWrapHeaders: true,
      manualSortBy: true,
      manualPagination: true,
      pageCount: -1,
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const onUpdateStatus = (status, onSuccess, onFailed) => async () => {
    const failed = [];

    for (const i of selectedFlatRows) {
      const { ManualJournalID } = i.original;

      try {
        await xero.patch(`manualjournals/${ManualJournalID}`, {
          ManualJournalID,
          Status: status,
        });
      } catch (ex) {
        console.error(ex);
        failed.push(ManualJournalID);
      }
    }

    if (failed.length) {
      onFailed(failed);
    } else {
      onSuccess();
    }

    await refetch();
  };

  const onPost = onUpdateStatus(
    "POSTED",
    () => NotificationManager.success(t("Posted successfully")),
    (failed) =>
      NotificationManager.error(
        t("{{ num }} failed to post", { num: failed.length })
      )
  );

  const onDelete = onUpdateStatus(
    "DELETED",
    () => NotificationManager.success(t("Deleted successfully")),
    (failed) =>
      NotificationManager.error(
        t("{{ num }} failed to delete", { num: failed.length })
      )
  );

  const onVoid = onUpdateStatus(
    "VOIDED",
    () => NotificationManager.success(t("Voided successfully")),
    (failed) =>
      NotificationManager.error(
        t("{{ num }} failed to void", { num: failed.length })
      )
  );

  const { selectedFlatRows, state } = table;
  const { pageIndex, sortBy, pageSize } = state;


  const { refetch } = useQuery(
    ["manualjournals", { pageIndex, status, sortBy, pageSize, reload }],
    () => {
      setIsLoading(true)
      history.push({
        pathname: `${window.location.pathname}`,
        search: `?pageSize=${pageSize}&page=${pageIndex}`
      })
      const order = sortBy[0]
        ? `${sortBy[0].id} ${sortBy[0].desc ? "DESC" : "ASC"}`
        : undefined;
      const page = pageIndex + 1;
      
      let query = search?.description ? `(Narration != null AND Narration.ToLower().Contains("${search.description?.replace(/"/g, "").toLowerCase()}"))` : ''
      query += search?.start_date ? (query !== '' ? ' AND ' : '') + `Date>=DateTime(${formatXeroDate(search.start_date)})` : ''
      query += search?.end_date ? (query !== '' ? ' AND ' : '') + `Date<=DateTime(${formatXeroDate(search.end_date)})` : ''
      return xero
        .get("manualjournals", {
          params: {
            page,
            Status: status !== "all" ? status.toUpperCase() : undefined,
            raw:
              status !== "all"
                ? `Status="${status.toUpperCase()}"` + (query? ` AND ${query}` : '')
                : query,
            order,
            pageSize,
          },
        })
        .get("data")
    },
    {
      select: produce((draft) => {
        for (const i of draft) {
          i.Credit = sumBy(i.JournalLines, (j) =>
            j.LineAmount < 0 ? j.LineAmount * -1 : 0
          );
          i.Debit = sumBy(i.JournalLines, (j) =>
            j.LineAmount > 0 ? j.LineAmount : 0
          );
        }
      }),
      onSuccess: (data) => {
        extraHandling(data)
        setIsLoading(false)
      },
    }
  );

  const uploadFile = async (file) => {
    try {
      const content = await readFileAsync(file);
      setIsLoading(true)
      const parsedData = parseXlsx(content);
      const filteredData = parsedData.filter((row) => {
        return row.Narration !== undefined && row.Date !== undefined && row.JournalLines?.[0]?.LineAmount !== undefined;
      });
      const response = await xero.post('manualjournals', filteredData);
      setIsLoading(false)
      NotificationManager.success(t('ManualJournals uploaded successfully'));

      setTimeout(() => {
        setIsModalOpen(false);
        refetch()
      }, 800);
    } catch (error) {
      setIsLoading(false)
      let errorCount = 0;

      if (error && error.response && error.response.data && error.response.data.detail) {
        error.response.data.detail.forEach((detail, index) => {
          if (errorCount < 3) {
            const errorMessage = `${index + 1}. ${detail}`;
            NotificationManager.error(errorMessage, 'Error');
            console.error(errorMessage);
            errorCount++;
          }
        });
      } else {
        if (errorCount < 3) {
          const errorMessage = t('Error uploading file, check console for more info.');
          NotificationManager.error(errorMessage);
          console.error(errorMessage);
          errorCount++;
        }
      }
    }
  };


  const parseXlsx = (xlsxContent) => {
    const data = new Uint8Array(xlsxContent);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });

    const headerRow = jsonData[0];

    return jsonData.slice(1).map((row) => {
      const statusFound = row[headerRow.indexOf("Status")]
      const status = (statusFound && typeof statusFound === 'string') ? (statusFound.toUpperCase() === 'POSTED' ? 'POSTED' : 'DRAFT') : 'DRAFT';
      return {
        Date: row[headerRow.indexOf("Date*")],
        Narration: row[headerRow.indexOf("Narration*")],
        Status: status,
        JournalLines: [
          {
            LineAmount: row[headerRow.indexOf("DebitAmount*")],
            AccountCode: row[headerRow.indexOf("DebitAccountCode*")],
            Description: row[headerRow.indexOf("DebitDescription")],
            TaxType: row[headerRow.indexOf("DebitTaxType")],
          },
          {
            LineAmount: -row[headerRow.indexOf("CreditAmount*")],
            AccountCode: row[headerRow.indexOf("CreditAccountCode*")],
            Description: row[headerRow.indexOf("CreditDescription")],
            TaxType: row[headerRow.indexOf("CreditTaxType")],
          },
        ],
      };
    });
  };

  const getStatusLength = (length, Type) => loading ? t(`Loading...`) : length + " " + t(Type) + " " + t(`Manual Journal${length !== 1 ? "s" : ""}`);

  const statusTitleCase = status === "all" ? "Total" : status?.charAt(0).toUpperCase() + status?.slice(1)

  const allList = allManualJournals?.Posted.concat(allManualJournals?.Draft);

  const selectedList = status === "all" ? allList : (allManualJournals?.[statusTitleCase] ?? allManualJournals?.Others);

  const selectedTotalMessage = getStatusLength(selectedList?.length, statusTitleCase);

  const handleNavClick = (tableId) => {
    history.push(tableId);
  };

  const tabs = [
    { id: 'all', label: t('All'), count: allList?.length },
    { id: 'draft', label: t('Draft'), count: allManualJournals?.Draft.length },
    { id: 'posted', label: t('Posted'), count: allManualJournals?.Posted.length },
    { id: 'voided', label: t('Voided'), count: allManualJournals?.Voided.length },
    { id: 'archived', label: t('Archived'), count: allManualJournals?.Archived.length }
  ];

  return (
    <Page title="Manual Journals" className="inv-tabs nw-list">
      <BackButton />
      <div className="tw-flex mb-4">
        <Link className="btn btn-primary ml-0" to={`${match.url}/new`}>
          <span className="fa fa-plus mr-2" />
          <Trans>Add Manual Journal</Trans>
        </Link>
        <Button color="primary" onClick={openModal}>
          <i className="fas fa-file-excel mr-2" /><Trans>Import Manual Journals</Trans>
        </Button>
        <div>
          <ImportModal
            isOpen={isModalOpen}
            toggle={closeModal}
            title="ManualJournals"
            importantNotes={importantNotes}
            handleUpload={uploadFile}
          />
        </div>
      </div>
      <Nav tabs className="nw-tabs2 inv-tabs my-3">
        {tabs.map(tab => (
          <div
            key={tab.id}
            onClick={() => handleNavClick(tab.id)}
            className={`${status === tab.id ? 'active-tabNum ' : ''}`}
          >
            {tab.label} {renderCountSpan(tab.count)}
          </div>
        ))}
      </Nav>
      <div className="search-container mb-0">
        <Row className="">
          <div className="col-4">
            <FormGroup className="text-nowrap">
              <Label><Trans>Search by Narration</Trans></Label>
              <Input name="name" type="text" value={search.description} onChange={(evt) => { setSearch({ ...search, description: evt.target.value }) }} />
            </FormGroup>
          </div>
          <div className="col-3">
            <FormGroup className="text-nowrap">
              <Label><Trans>Start Date</Trans></Label>
              <DatePicker className="form-control form-control-sm" selected={search.start_date} onChange={(date) => { setSearch({ ...search, start_date: date }) }} />
            </FormGroup>
          </div>
          <div className="col-3">
            <FormGroup className="text-nowrap">
              <Label><Trans>End Date</Trans></Label>
              <DatePicker className="form-control form-control-sm" selected={search.end_date} onChange={(date) => { setSearch({ ...search, end_date: date }) }} />
            </FormGroup>
          </div>
          <div className="auto">
            <div className="text-nowrap" style={{ paddingTop: "1.7rem" }}>
              <Button onClick={()=> setReload(!reload)}> <Trans>Search</Trans> </Button>
              <Button color={'danger'} onClick={(evt) => { setSearch({...search, description: "", start_date: "", end_date: ""}); setReload(!reload) }}> <Trans>Clear</Trans> </Button>
            </div>
          </div>
        </Row>
      </div>

      <div className="row my-3">
        <div className="col-6">
          {status === "draft" &&
            <>
              <Button
                color="secondary"
                size="sm"
                onClick={togglePost}
                disabled={!selectedFlatRows.length}
                className="ml-0"
              >
                <Trans>Post</Trans>
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={toggleDelete}
                disabled={!selectedFlatRows.length}
              >
                <Trans>Delete</Trans>
              </Button>
            </>
          }
          {status === "posted" &&
            <Button
              color="secondary"
              size="sm"
              onClick={toggleVoid}
              disabled={!selectedFlatRows.length}
              className="ml-0"
            >
              <Trans>Void</Trans>
            </Button>
          }

        </div>
        <div className="col-6 text-right">
          <div className="text-muted">
            <span>{selectedTotalMessage}</span>
          </div>
        </div>
      </div>

      <PostModal onConfirm={onPost}>
        <ModalHeader>
          <Trans>Confirm Post</Trans>
        </ModalHeader>
        <ModalBody>
          <Trans>
            You have selected {{ num: selectedFlatRows.length }} items to post.
          </Trans>
        </ModalBody>
      </PostModal>
      <VoidModal onConfirm={onVoid}>
        <ModalHeader>
          <Trans>Confirm Void</Trans>
        </ModalHeader>
        <ModalBody>
          <Trans>
            You have selected {{ num: selectedFlatRows.length }} items to void.
          </Trans>
        </ModalBody>
      </VoidModal>
      <DeleteModal onConfirm={onDelete}>
        <ModalHeader>
          <Trans>Confirm Delete</Trans>
        </ModalHeader>
        <ModalBody>
          <Trans>
            You have selected {{ num: selectedFlatRows.length }} items to
            delete.
          </Trans>
        </ModalBody>
      </DeleteModal>
      {isLoading ? <div className="" style={{ fontSize: "0.6rem" }}>
        <LoadingMsg />
      </div> :
        <Table table={table} size="sm" className="inv-table no-wrap v-middle" responsive />}
      <XeroPagination table={table} className="inv-table" />
    </Page>
  );
};
